import _ from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  fetchUiState,
  saveUiState,
} from '../../../../infrastructure/api/ui-states'
import useWindowUnloadEffect from '../../../hooks/useWindowUnloadEffect'

const EXPANDED_ROWS_STATE_KEY = 'PROJECT_PLAN_EXPANDED_ROWS_STATE'

type StoredExpandedRowsState = {
  expandedRowIds?: string[]
}

type ExpandedRowsState = {
  key: string
  expandedRowIds: string[]
  hasRestorableState: boolean
  isChangedByUser: boolean
  // 取得に失敗したルートで保存すると、保存済みの展開状態を既定の展開で上書きしてしまう
  isSaveDisabled: boolean
}

type OperationBeforeLoad = (expandedRowIds: string[]) => string[]

const fetchExpandedRowIds = async (
  functionUuid: string,
  key: string
): Promise<string[] | undefined> =>
  (await fetchUiState<StoredExpandedRowsState>(functionUuid, key))
    ?.expandedRowIds

const addRowIds = (expandedRowIds: string[], rowIds: string[]) =>
  _.uniq([...expandedRowIds, ...rowIds])

const removeRowIds = (expandedRowIds: string[], rowIds: string[]) =>
  expandedRowIds.filter(id => !rowIds.includes(id))

/**
 * @param legacyExpandedRowIds 旧キーを使わないルートでは undefined を渡す
 */
export const buildLoadedExpandedRowsState = (
  key: string,
  savedRowIds: string[] | undefined,
  isFetchFailed: boolean,
  legacyExpandedRowIds: string[] | undefined,
  operationsBeforeLoad: OperationBeforeLoad[]
): ExpandedRowsState => {
  const restorableRowIds =
    savedRowIds ??
    (_.isEmpty(legacyExpandedRowIds) ? undefined : legacyExpandedRowIds)
  return {
    key,
    expandedRowIds: operationsBeforeLoad.reduce(
      (rowIds, operate) => operate(rowIds),
      restorableRowIds ?? []
    ),
    hasRestorableState: !!restorableRowIds,
    isChangedByUser: operationsBeforeLoad.length > 0,
    isSaveDisabled: isFetchFailed,
  }
}

/**
 * @param legacyExpandedRowIds ルートごとに保存する前の、プロジェクト単位の展開状態。
 * 取得が終わるまでは undefined を渡す
 */
export const useExpandedRowsState = (
  functionUuid: string,
  projectUuid: string,
  rootProjectPlanUuid: string | undefined,
  legacyExpandedRowIds: string[] | undefined
): {
  initialized: boolean
  hasRestorableState: boolean
  expandedRowIds: string[]
  expandRows: (rowIds: string[]) => void
  collapseRows: (rowIds: string[]) => void
  addDefaultExpandedRows: (rowIds: string[]) => void
  removeRowsWithoutChildren: (rowIds: string[]) => void
  saveImmediately: () => void
} => {
  const [state, setState] = useState<ExpandedRowsState>()
  // 別のルートから戻ったときは、保存待ちがまだサーバに届いていないことがあるため
  // 取得し直さずに手元の状態を使う
  const statesByKey = useRef(new Map<string, ExpandedRowsState>())
  if (state) statesByKey.current.set(state.key, state)
  // 読み込みが終わる前の開閉は、読み込んだ展開状態に重ねて反映する
  const operationsBeforeLoad = useRef<OperationBeforeLoad[]>([])

  const stateKey = `${EXPANDED_ROWS_STATE_KEY}-${
    rootProjectPlanUuid ?? projectUuid
  }`
  const currentRootState = state?.key === stateKey ? state : undefined
  const initialized = !!currentRootState
  const isLegacyStateRequired = !rootProjectPlanUuid
  const legacyExpandedRowIdsOfRoot = isLegacyStateRequired
    ? legacyExpandedRowIds
    : undefined

  // ルートを切り替えた直後の保存が、前のルートの保存待ちを打ち消さないようにキーごとに待つ
  const savers = useRef(
    new Map<string, _.DebouncedFunc<(expandedRowIds: string[]) => void>>()
  )
  const save = useCallback(
    (key: string, expandedRowIds: string[]) => {
      let saver = savers.current.get(key)
      if (!saver) {
        saver = _.debounce((rowIds: string[]) => {
          saveUiState<StoredExpandedRowsState>(functionUuid, key, {
            expandedRowIds: rowIds,
          })
        }, 3000)
        savers.current.set(key, saver)
      }
      saver(expandedRowIds)
    },
    [functionUuid]
  )

  useEffect(() => {
    const loadedState = statesByKey.current.get(stateKey)
    if (loadedState) {
      setState(loadedState)
      return
    }
    if (isLegacyStateRequired && !legacyExpandedRowIdsOfRoot) return
    operationsBeforeLoad.current = []
    let cancelled = false
    const restore = async () => {
      let savedRowIds: string[] | undefined
      let isFetchFailed = false
      try {
        savedRowIds = await fetchExpandedRowIds(functionUuid, stateKey)
      } catch (e) {
        isFetchFailed = true
      }
      if (cancelled) return
      const operations = operationsBeforeLoad.current
      operationsBeforeLoad.current = []
      setState(
        buildLoadedExpandedRowsState(
          stateKey,
          savedRowIds,
          isFetchFailed,
          legacyExpandedRowIdsOfRoot,
          operations
        )
      )
    }
    restore()
    return () => {
      cancelled = true
    }
  }, [
    functionUuid,
    stateKey,
    isLegacyStateRequired,
    legacyExpandedRowIdsOfRoot,
  ])

  // 別のルートから戻ったときは statesByKey にあった配列がそのまま戻るため、保存し直さない
  const lastSavedRowIds = useRef(new Map<string, string[]>())
  useEffect(() => {
    if (!state?.isChangedByUser || state.isSaveDisabled) return
    if (lastSavedRowIds.current.get(state.key) === state.expandedRowIds) return
    lastSavedRowIds.current.set(state.key, state.expandedRowIds)
    save(state.key, state.expandedRowIds)
  }, [save, state])

  const saveImmediately = useCallback(
    () => savers.current.forEach(saver => saver.flush()),
    []
  )

  useWindowUnloadEffect(saveImmediately, false)

  const expandRows = useCallback(
    (rowIds: string[]) => {
      if (!initialized) {
        operationsBeforeLoad.current.push(ids => addRowIds(ids, rowIds))
        return
      }
      setState(prev => {
        if (
          prev?.key !== stateKey ||
          rowIds.every(id => prev.expandedRowIds.includes(id))
        ) {
          return prev
        }
        return {
          ...prev,
          expandedRowIds: addRowIds(prev.expandedRowIds, rowIds),
          isChangedByUser: true,
        }
      })
    },
    [initialized, stateKey]
  )

  const collapseRows = useCallback(
    (rowIds: string[]) => {
      if (!initialized) {
        operationsBeforeLoad.current.push(ids => removeRowIds(ids, rowIds))
        return
      }
      setState(prev => {
        if (
          prev?.key !== stateKey ||
          !rowIds.some(id => prev.expandedRowIds.includes(id))
        ) {
          return prev
        }
        return {
          ...prev,
          expandedRowIds: removeRowIds(prev.expandedRowIds, rowIds),
          isChangedByUser: true,
        }
      })
    },
    [initialized, stateKey]
  )

  // 未保存時に既定で開いた行は、利用者が開閉するまで保存しない。
  // 記録しておくことで、あとから開いた行と一緒に保存したときに既定の展開も残り、
  // 取得を待たずに同じルートへ戻ったときは既定の展開をやり直さず記録から復元する
  const addDefaultExpandedRows = useCallback(
    (rowIds: string[]) => {
      setState(prev =>
        prev?.key === stateKey
          ? {
              ...prev,
              expandedRowIds: addRowIds(prev.expandedRowIds, rowIds),
              hasRestorableState: true,
            }
          : prev
      )
    },
    [stateKey]
  )

  // 保存後に子がなくなった行を除くだけで、利用者の開閉ではないため保存しない
  const removeRowsWithoutChildren = useCallback(
    (rowIds: string[]) => {
      setState(prev =>
        prev?.key === stateKey
          ? {
              ...prev,
              expandedRowIds: removeRowIds(prev.expandedRowIds, rowIds),
            }
          : prev
      )
    },
    [stateKey]
  )

  return useMemo(
    () => ({
      initialized,
      hasRestorableState: !!currentRootState?.hasRestorableState,
      expandedRowIds: currentRootState?.expandedRowIds ?? [],
      expandRows,
      collapseRows,
      addDefaultExpandedRows,
      removeRowsWithoutChildren,
      saveImmediately,
    }),
    [
      initialized,
      currentRootState,
      expandRows,
      collapseRows,
      addDefaultExpandedRows,
      removeRowsWithoutChildren,
      saveImmediately,
    ]
  )
}
